// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import {
  asignacionDe,
  asignar,
  desasignar,
  habitualDe,
} from "../../services/asignacion";
import { claveInterno, tractorDelChofer } from "../../shared/asignacion";
import { diaAr } from "../../shared/fechaAr";
import { useAvisarVacio } from "./BloqueFicha";

/* «Chofer XXX > tractor YYY», en la ficha de la persona.
 *
 * Pedido de Nico: que el hub diga qué camión tiene cada chofer, que se pueda
 * editar a mano, y que preguntando por el camión aparezca el chofer.
 *
 * ── ACÁ SE EDITA, Y EN LA FICHA DEL CAMIÓN NO ────────────────────────────
 *
 * La fila vive en `chofer_tractor` y su clave es el DNI: una asignación es de
 * una persona. Poner el mismo formulario también del lado del camión daría
 * **dos lugares para cargar lo mismo** — que es exactamente lo que el
 * encabezado de la ficha de la unidad dice que no se hace, y por lo que ya se
 * pagó con los dos talleres y con la agenda de turnos. Del otro lado se ve
 * quién lo tiene, con un enlace para venir a cambiarlo acá.
 *
 * ── LO ASIGNADO Y LO HABITUAL NO SE MEZCLAN ──────────────────────────────
 *
 * La regla de cuál gana está en `shared/asignacion.js`, aparte y probada en
 * una tabla: manda lo que escribió una persona, y la tendencia de portería se
 * sigue viendo cuando dice otra cosa. Tapando la que pierde, una asignación
 * que quedó vieja se queda mal para siempre y nadie se entera.
 *
 * NO FRENA NADA: estado propio, y si la consulta falla la ficha abre igual.
 */
const TractorAsignado = ({ dni, puedeCargar = false }) => {
  /* CUATRO ESTADOS Y NO TRES, y los dos del medio son los que importan:
   *
   *   undefined  todavía no llegó
   *   false      NO SE PUDO CONSULTAR
   *   null       llegó, y no tiene ninguno asignado
   *   {…}        la fila
   *
   * `false` y `null` dibujados igual dirían «Nadie se lo asignó» cuando la
   * consulta se cayó — que es afirmar algo que no se sabe, sobre la única
   * pregunta que este bloque vino a contestar. */
  const [asignado, setAsignado] = useState(undefined);
  const [habitual, setHabitual] = useState(undefined);
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState("");
  const [nota, setNota] = useState("");
  const [yendo, setYendo] = useState(false);
  const [error, setError] = useState("");

  const traer = useCallback(() => {
    if (!dni) return;
    try {
      Promise.resolve(asignacionDe(dni))
        .then(setAsignado)
        .catch(() => setAsignado(false));
    } catch {
      setAsignado(false);
    }
  }, [dni]);

  useEffect(() => {
    let vigente = true;
    if (!dni) return undefined;
    /* EL `try` ENVUELVE A LA LLAMADA Y NO SÓLO A LA PROMESA: un `.catch()` no
     * atrapa lo que revienta antes de devolverla. Lo encontró un test en el
     * RTO, está escrito en `useBloque` de la ficha, y vale igual acá. */
    try {
      Promise.resolve(asignacionDe(dni))
        .then((r) => vigente && setAsignado(r))
        .catch(() => vigente && setAsignado(false));
    } catch {
      setAsignado(false);
    }
    /* La tendencia va en su propia promesa: si `tractor_habitual` no contesta,
     * la asignación —que es el dato que pidió Nico— tiene que salir igual. */
    try {
      Promise.resolve(habitualDe(dni))
        .then((r) => vigente && setHabitual(r))
        .catch(() => vigente && setHabitual(null));
    } catch {
      setHabitual(null);
    }
    return () => {
      vigente = false;
    };
  }, [dni]);

  const guardar = async (ev) => {
    ev.preventDefault();
    setYendo(true);
    setError("");
    try {
      await asignar(dni, texto, { nota });
      setAbierto(false);
      traer();
    } catch (e) {
      setError(e.message || "No se pudo guardar");
    } finally {
      setYendo(false);
    }
  };

  const sacar = async () => {
    setYendo(true);
    setError("");
    try {
      await desasignar(dni);
      setAbierto(false);
      traer();
    } catch (e) {
      setError(e.message || "No se pudo sacar");
    } finally {
      setYendo(false);
    }
  };

  /* «Nadie se lo asignó» y portería tampoco propone uno: en la ficha de la
   * persona el bloque no se dibuja (Igna, 02/10: lo vacío no aparece).
   *
   * Se espera a las DOS respuestas antes de decirlo: con la asignación vacía y
   * la tendencia todavía viajando, el bloque se escondería y a los 200 ms
   * volvería a aparecer con la propuesta.
   *
   * Y A QUIEN PUEDE CARGAR SE LE SIGUE MOSTRANDO, por lo mismo que las multas:
   * para él no es un dato vacío, es el botón de asignarle uno. Escondido, la
   * única forma de darle un tractor a un chofer nuevo desaparecería justo
   * cuando hace falta. */
  useAvisarVacio(
    asignado === undefined || habitual === undefined
      ? undefined
      : asignado === null &&
          !puedeCargar &&
          tractorDelChofer(null, habitual).estado === "nada",
  );

  /* Mientras carga no se dibuja: aparecer y cambiar de texto a los 200 ms se
   * lee como que la pantalla se corrigió sola. */
  if (asignado === undefined) return null;

  /* No se pudo consultar: se dice eso y NADA MÁS. Cayendo igual al resto del
   * dibujo, la pantalla contestaría «Nadie se lo asignó» — o peor, propondría
   * el habitual como si fuera la única opción— sobre una persona que capaz
   * tiene camión hace dos años. */
  const noSePudo = asignado === false;
  const r = tractorDelChofer(noSePudo ? null : asignado, noSePudo ? null : habitual);
  const abrir = () => {
    setTexto(r.estado === "propuesta" ? r.interno : r.interno || "");
    setNota(asignado?.nota || "");
    setError("");
    setAbierto(true);
  };

  return (
    <div className="ficha-asignado">
      <span className="et">Tractor asignado</span>

      {!noSePudo && r.estado === "nada" && (
        <span className="ficha-asignado-txt tenue">Nadie se lo asignó.</span>
      )}

      {/* LA PROPUESTA NO SE DIBUJA COMO UN HECHO. Portería dice que anda
          siempre con el mismo, pero eso no lo declaró nadie — y si se mostrara
          igual que una asignación, el que lo lea va a creer que alguien lo
          decidió. Un click de una persona, y ahí sí lo es. */}
      {!noSePudo && r.estado === "propuesta" && (
        <span className="ficha-asignado-txt tenue">
          Nadie se lo asignó. Suele salir con el{" "}
          <Link to={`/unidad/tractor/${r.interno}`}>{r.interno}</Link>
          {habitual?.salidas != null && (
            <em className="tk-item-meta">
              {` · ${habitual.salidas} de ${habitual.total} salidas`}
            </em>
          )}
        </span>
      )}

      {(r.estado === "asignado" || r.estado === "discrepa") && (
        <span className="ficha-asignado-txt">
          <b>
            <Link to={`/unidad/tractor/${r.interno}`}>{r.interno}</Link>
          </b>
          {r.desde && (
            <em className="tk-item-meta">
              {` · desde ${diaAr(r.desde)}`}
            </em>
          )}
          {r.nota && <em className="tk-item-meta"> · {r.nota}</em>}
        </span>
      )}

      {/* La discrepancia se MUESTRA y no se resuelve. Puede ser un reemplazo de
          dos semanas o una asignación que quedó vieja, y eso lo sabe la
          persona que lo lee, no la pantalla. */}
      {r.estado === "discrepa" && (
        <span className="ficha-asignado-nota">
          Pero últimamente sale con el{" "}
          <Link to={`/unidad/tractor/${claveInterno(habitual.tractor)}`}>
            {claveInterno(habitual.tractor)}
          </Link>
          {habitual?.salidas != null &&
            ` (${habitual.salidas} de ${habitual.total} salidas)`}
          .
        </span>
      )}

      {/* «No pude consultar» no se dice como «no tiene». */}
      {noSePudo && (
        <span className="ficha-asignado-nota">
          No se pudo consultar.
        </span>
      )}

      {/* Sin saber qué tiene, no se ofrece cambiárselo: el formulario abriría
          en blanco y guardar pisaría una asignación que sigue estando. */}
      {puedeCargar && !abierto && !noSePudo && (
        <button type="button" className="rto-link" onClick={abrir}>
          {r.estado === "asignado" || r.estado === "discrepa"
            ? "Cambiar el tractor"
            : r.estado === "propuesta"
              ? `Asignarle el ${r.interno}`
              : "Asignarle un tractor"}
        </button>
      )}

      {abierto && (
        <form className="rto-declarada__form" onSubmit={guardar}>
          <label>
            <span className="et">Interno</span>
            <input
              type="text"
              inputMode="numeric"
              value={texto}
              onChange={(ev) => setTexto(ev.target.value)}
              aria-label="Interno del tractor"
              autoFocus
            />
          </label>
          <label>
            <span className="et">Nota</span>
            <input
              type="text"
              value={nota}
              placeholder="mientras el titular está de licencia"
              onChange={(ev) => setNota(ev.target.value)}
              aria-label="Nota"
            />
          </label>
          <div className="rto-declarada__botones">
            <button className="tk-boton" disabled={yendo || !claveInterno(texto)}>
              Guardar
            </button>
            <button
              type="button"
              className="rto-link"
              onClick={() => setAbierto(false)}
            >
              Cancelar
            </button>
            {asignado && !noSePudo && (
              <button
                type="button"
                className="rto-link rto-ojo"
                onClick={sacar}
                disabled={yendo}
              >
                Sacárselo
              </button>
            )}
          </div>
          {/* El error se dice con su motivo y el formulario NO se cierra:
              cerrado, «no tenés permiso» y «se guardó» se ven igual. */}
          {error && <p className="tk-item-meta rto-ojo">{error}</p>}
        </form>
      )}
    </div>
  );
};

export default TractorAsignado;
